import config from 'config';
import Logger from 'utils/logger';
import { Account } from 'types/Account';
import { User } from 'types/User';

declare global {
  interface Window {
    heap?: {
      appid?: string;
      config?: Record<string, unknown>;
      load: (id: string) => void;
      identify: (userId: string) => void;
      addUserProperties: (properties: Record<string, any>) => void;
      addEventProperties: (properties: Record<string, any>) => void;
      clearEventProperties: () => void;
      track: (eventName: string, properties?: Record<string, any>) => void;
      push: (args: unknown[]) => void;
    };
  }
}

function getAccountState(account: Account) {
  if (account.isExpired) {
    return account.isTrial ? 'expired_trial' : 'churned';
  }

  return account.isTrial ? 'trial' : 'subscribed';
}

const HeapUtils = {
  shouldSkip() {
    return !config.HEAP_ID;
  },
  loadScript() {
    try {
      if (this.shouldSkip()) {
        Logger.log('Skipping Heap script load');
        return;
      }

      const heapId = config.HEAP_ID;

      window.heap = window.heap || ([] as any);
      window.heap!.load = function (e: string) {
        window.heap!.appid = e;
        window.heap!.config = {};
        const r = document.createElement('script');
        r.type = 'text/javascript';
        r.async = true;
        r.src = 'https://cdn.heapanalytics.com/js/heap-' + e + '.js';
        const a = document.getElementsByTagName('script')[0];
        a?.parentNode?.insertBefore(r, a);
        const methods = [
          'addEventProperties',
          'addUserProperties',
          'clearEventProperties',
          'identify',
          'resetIdentity',
          'removeEventProperty',
          'setEventProperties',
          'track',
          'unsetEventProperty',
        ];
        for (const method of methods) {
          (window.heap as any)[method] = function (...args: unknown[]) {
            window.heap!.push([method, ...args]);
          };
        }
      };
      window.heap!.load(heapId);
    } catch (e) {
      Logger.error(e);
    }
  },
  identifyUser(account: Account, user: User) {
    try {
      if (this.shouldSkip()) {
        Logger.log('Skipping Heap user identification');
        return;
      }

      if (
        typeof window.heap?.identify !== 'function' ||
        typeof window.heap?.addUserProperties !== 'function'
      ) {
        return;
      }

      const userIdentification = `${account.publicId}-${user.id}`;
      const userProperties = {
        id: userIdentification,
        accountId: account.accountId,
        accountPublicId: account.publicId,
        name: user.name,
        role: user.role,
        company: user.intercom?.company.name,
        isTrial: account.isTrial,
        isSubscription: account.isSubscription,
        usingNewAnalytics: user.intercom?.usingNewAnalytics,
        usingNewCalendar: user.intercom?.usingNewCalendar,
        planLabel: account.plan.label,
      };

      window.heap.identify(userIdentification);
      window.heap.addUserProperties(userProperties);
      Logger.log('Setting heap user properties: %o', userProperties);
    } catch (e) {
      Logger.error(e);
    }
  },
  setEventProperties(account: Account) {
    try {
      if (this.shouldSkip()) {
        Logger.log('Skipping Heap event properties');
        return;
      }

      if (
        typeof window.heap?.addEventProperties !== 'function' ||
        typeof window.heap?.clearEventProperties !== 'function'
      ) {
        return;
      }

      const accountState = getAccountState(account);
      const properties = {
        'Account state': accountState,
        'Account has profile connected': !!account.plan.pageCount,
        'Pricing plan': account.plan.label || 'none',
        'New app': 'true',
      };

      window.heap.clearEventProperties();
      window.heap.addEventProperties(properties);
      Logger.log('Setting heap event properties: %o', properties);
    } catch (e) {
      Logger.error(e);
    }
  },
  updatePricingPlan(planName: string) {
    try {
      if (this.shouldSkip()) {
        return;
      }

      const plan = planName.trim();
      if (!plan) {
        return;
      }

      if (
        typeof window.heap?.addEventProperties !== 'function' ||
        typeof window.heap?.addUserProperties !== 'function'
      ) {
        return;
      }

      window.heap.addEventProperties({ 'Pricing plan': plan });
      window.heap.addUserProperties({ planLabel: plan });
      Logger.log('Updating heap pricing plan: %s', plan);
    } catch (e) {
      Logger.error(e);
    }
  },
  track(eventName: string, properties?: Record<string, any>) {
    try {
      if (this.shouldSkip() || typeof window.heap?.track !== 'function') {
        return;
      }

      window.heap.track(eventName, properties);
    } catch (e) {
      Logger.error(e);
    }
  },
  toAnswer(values: string[]): string {
    return [...values].sort().join(',');
  },
  events: {
    AI_CONTENT_COPY_TEXT_TO_CLIPBOARD: 'ai-content_copy-text-to-clipboard',
    SOCIAL_LOGIN_FETCH_DATA_ERROR: 'social-login_fetch-data-error',
    SOCIAL_LOGIN_AUTHORIZE_ERROR: 'social-login_authorize-error',
    SOCIAL_LOGIN_FETCH_CONNECTED_EXTERNAL_PAGES_ERROR:
      'social-login_fetch-connected-external-pages-data-error',
    AUTH_LOGIN_SUCCESS: 'auth-login_success',
    AUTH_LOGIN_ERROR: 'auth-login_error',
    AUTH_REGISTER_SUCCESS: 'auth-register_success',
    AUTH_REGISTER_ERROR: 'auth-register_error',
    CANCELLATION_FLOW_OPENED: 'cancellation_flow-opened',
    CANCELLATION_FLOW_STARTED: 'cancellation_flow-started',
    CANCELLATION_REASON_SELECTED: 'cancellation_reason-selected',
    CANCELLATION_COMPETITOR_SUBMITTED: 'cancellation_competitor-submitted',
    CANCELLATION_RETENTION_OFFER_SHOWN: 'cancellation_retention-offer-shown',
    CANCELLATION_RETENTION_ACCEPTED: 'cancellation_retention-accepted',
    CANCELLATION_COMPLETED: 'cancellation_completed',
    CANCELLATION_DISMISSED: 'cancellation_dismissed',
    ONBOARDING_STEP_REFERRAL_COMPLETED: 'onboarding_step_referral_completed',
    ONBOARDING_STEP_BUSINESS_TYPE_COMPLETED:
      'onboarding_step_business_type_completed',
    ONBOARDING_STEP_EXPECTATIONS_COMPLETED:
      'onboarding_step_expectations_completed',
    CONNECT_NETWORK_SELECTED: 'connect_network_selected',
    CONNECT_META_METHOD_SELECTED: 'connect_meta_method_selected',
    CONNECT_OAUTH_STARTED: 'connect_oauth_started',
    CONNECT_OAUTH_RETURNED: 'connect_oauth_returned',
    CONNECT_PORTFOLIOS_LISTED: 'connect_portfolios_listed',
    CONNECT_PROFILES_LISTED: 'connect_profiles_listed',
    CONNECT_PROFILES_SUBMITTED: 'connect_profiles_submitted',
    CONNECT_PROFILES_SAVED: 'connect_profiles_saved',
    CONNECT_PROFILES_SAVE_FAILED: 'connect_profiles_save_failed',
    CONNECT_RESULT_SHOWN: 'connect_result_shown',
    CONNECT_FLOW_CLOSED: 'connect_flow_closed',
    CHECKLIST_ITEM_CONNECT_PROFILES_COMPLETED:
      'checklist_item_connect_profiles_completed',
    CHECKLIST_ITEM_FIRST_POST_COMPLETED: 'checklist_item_first_post_completed',
    CHECKLIST_ITEM_SEND_FOR_APPROVAL_COMPLETED:
      'checklist_item_send_for_approval_completed',
    CHECKLIST_ITEM_INVITE_TEAMMATE_COMPLETED:
      'checklist_item_invite_teammate_completed',
    CHECKLIST_COMPLETED: 'checklist_completed',
  },
};

export default HeapUtils;
